import { useEffect, useMemo, useState } from "react";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { toast } from "sonner";
import { Sparkle } from "@phosphor-icons/react";

/** New checklist template: pick a type, add specifics, AI builds the items (or start blank). */
export default function InspectionTemplateDialog({ open, onOpenChange, onCreated }) {
  const [meta, setMeta] = useState(null);
  const [typeKey, setTypeKey] = useState("");
  const [title, setTitle] = useState("");
  const [brief, setBrief] = useState("");
  const [frequency, setFrequency] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setTypeKey(""); setTitle(""); setBrief(""); setFrequency(""); API.get("/hse/inspections/types").then(({ data }) => setMeta(data)).catch(() => {}); } }, [open]);
  const type = useMemo(() => (meta?.types || []).find(t => t.key === typeKey), [meta, typeKey]);

  const create = async (ai) => {
    setBusy(true);
    try {
      const { data } = await API.post("/hse/inspections/templates", { type_key: typeKey, title: title || undefined, brief, frequency: frequency || undefined, ai });
      toast.success(ai ? "The AI is building your checklist…" : "Blank checklist created");
      onCreated?.(data);
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="insp-template-dialog">
        <DialogHeader>
          <DialogTitle className="font-[Chivo]">New inspection checklist</DialogTitle>
          <p className="text-xs text-muted-foreground">Pick the kind of check — the AI writes the items so a PASS always means safe. You can edit every item before activating it.</p>
        </DialogHeader>
        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
          {(meta?.types || []).map(t => (
            <button key={t.key} type="button" onClick={() => { setTypeKey(t.key); setFrequency(t.frequency); }} data-testid={`insp-type-${t.key}`}
              className={`text-left border rounded-md p-2.5 transition-colors ${typeKey === t.key ? "border-primary bg-primary/5" : "border-border hover:bg-muted/40"}`}>
              <div className="text-xs font-semibold leading-tight">{t.label}</div>
              <p className="text-[10px] text-muted-foreground mt-0.5 line-clamp-2">{t.blurb}</p>
            </button>
          ))}
        </div>
        {type && (
          <div className="grid gap-3 sm:grid-cols-2 mt-2" data-testid="insp-template-form">
            <div className="space-y-1.5"><Label className="text-xs">Title {type.key === "custom" ? "*" : "(optional)"}</Label><Input value={title} onChange={e => setTitle(e.target.value)} placeholder={type.label} className="h-9" data-testid="insp-template-title" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Default frequency</Label>
              <select value={frequency} onChange={e => setFrequency(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" data-testid="insp-template-frequency">
                {(meta?.frequencies || []).map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
              </select></div>
            <div className="sm:col-span-2 space-y-1.5"><Label className="text-xs">Anything specific to check {type.key === "custom" ? "*" : ""}</Label>
              <Textarea value={brief} onChange={e => setBrief(e.target.value)} rows={3} placeholder={type.key === "custom" ? "Describe what the checklist must cover…" : "e.g. our vans carry ladders on roof racks and a gas bottle for brazing"} data-testid="insp-template-brief" /></div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button variant="outline" disabled={!type || busy} onClick={() => create(false)} data-testid="insp-template-create-blank">Start blank</Button>
          <Button disabled={!type || busy || (type?.key === "custom" && !title && !brief)} onClick={() => create(true)} data-testid="insp-template-create-ai"><Sparkle size={14} className="mr-1" /> Build with AI</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
